import { absurd, pipe } from 'fp-ts/function'
import { sequenceS } from 'fp-ts/lib/Apply'
import { Applicative, flatMap, fromNullable, map, none, some, type Option } from 'fp-ts/Option'
import { head, filter } from 'fp-ts/ReadonlyArray'

import { type Money, type BackendPlan, type PlansData } from '@lib/services/server-data'

import { type PlansPageFlag, type PlanKind, PlansPageFlags } from '../types'

interface PlanMonthlyPrices {
  readonly whenBilledAnnually: string
  readonly whenBilledMonthly: string
}

export const isAnnualPlan = (plan: BackendPlan): boolean => plan.periodMonths === 12
export const isMonthlyPlan = (plan: BackendPlan): boolean => plan.periodMonths === 1

const whenBilledAnnually = (plans: ReadonlyArray<BackendPlan>): Option<Money> =>
  pipe(
    plans,
    filter(isAnnualPlan),
    head,
    map(plan => plan.regularPriceMoney),
    map(money => ({
      value: Math.round(money.value / 12),
      currency: money.currency,
    })),
  )

const freePlanBilledAnnually = (plans: ReadonlyArray<BackendPlan>): Option<Money> =>
  pipe(
    plans,
    filter(isAnnualPlan),
    head,
    map(plan => plan.regularPriceMoney),
    map(money => ({
      value: 0,
      currency: money.currency,
    })),
  )

const whenBilledMonthly = (plans: ReadonlyArray<BackendPlan>): Option<Money> =>
  pipe(
    plans,
    filter(isMonthlyPlan),
    head,
    map(plan => plan.regularPriceMoney),
  )

const monthlyPrice =
  (periodMapper: (plans: ReadonlyArray<BackendPlan>) => Option<Money>) =>
  (plans?: ReadonlyArray<BackendPlan>) =>
    pipe(plans, fromNullable, flatMap(periodMapper))

const planMonthlyPrices = (plans?: ReadonlyArray<BackendPlan>): Option<PlanMonthlyPrices> =>
  sequenceS(Applicative)({
    whenBilledMonthly: pipe(plans, monthlyPrice(whenBilledMonthly), map(moneyToString(false))),
    whenBilledAnnually: pipe(plans, monthlyPrice(whenBilledAnnually), map(moneyToString(true))),
  })

const zeroMonthlyPrices = (plans?: ReadonlyArray<BackendPlan>): Option<PlanMonthlyPrices> =>
  sequenceS(Applicative)({
    whenBilledMonthly: some(''),
    whenBilledAnnually: pipe(plans, monthlyPrice(freePlanBilledAnnually), map(moneyToString(true))),
  })

/**
 * We only support these 4 currencies for now, all other (unexpected) values should be ignored.
 * INR must have a space between the symbol and the value.
 */
const currencySymbolFromIsoCode = (code: string) => {
  switch (code) {
    case 'USD': {
      return '$'
    }
    case 'GBP': {
      return '£'
    }
    case 'EUR': {
      return '€'
    }
    case 'INR': {
      return '₹ '
    }
    default: {
      return ''
    }
  }
}

/**
 * Using suffix only for USD to mimic existing funnel's behavior when formatting currencies
 */
const maybeWithCurrencyCode = (code: string, withCurrencyCode: boolean) => {
  if (!withCurrencyCode) {
    return ''
  }

  switch (code) {
    case 'USD': {
      return ' USD'
    }
    default: {
      return ''
    }
  }
}

/**
 * TODO: Implement full copy of funnel's currency formatter.
 * It requires separate dependency 'numeral'
 */
const moneyToString =
  (withCurrencyCode: boolean) =>
  (money: Money): string =>
    [
      currencySymbolFromIsoCode(money.currency),
      money.value,
      maybeWithCurrencyCode(money.currency, withCurrencyCode),
    ].join('')

export const monthlyPrices = (
  planKind: PlanKind,
  plansData?: PlansData,
): Option<PlanMonthlyPrices> => {
  switch (planKind) {
    case 'business': {
      return planMonthlyPrices(plansData?.bundleBusinessPlans)
    }
    case 'pro': {
      return planMonthlyPrices(plansData?.bundleProPlans)
    }
    case 'free': {
      return zeroMonthlyPrices(plansData?.bundleProPlans ?? plansData?.bundleBusinessPlans)
    }
    case 'enterprise': {
      return none
    }
    default: {
      return absurd(planKind)
    }
  }
}

export const planKindToPageFlag = (kind: PlanKind): PlansPageFlag => {
  switch (kind) {
    case 'free': {
      return PlansPageFlags.DISABLE_FREE_CTA
    }
    case 'pro': {
      return PlansPageFlags.DISABLE_PRO_CTA
    }
    case 'business': {
      return PlansPageFlags.DISABLE_BUSINESS_CTA
    }
    case 'enterprise': {
      return PlansPageFlags.DISABLE_ENTERPRISE_CTA
    }
    default: {
      return absurd(kind)
    }
  }
}
